import React, { memo, useEffect, useRef, useState, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';

/* ═══════════════════════════════════════════════════════════════════════
   INLINE SVG ICONS
   ═══════════════════════════════════════════════════════════════════════ */
const ZapIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
);
const TargetIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" />
    </svg>
);
const SparklesIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L12 21l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
        <path d="M20 4v4" /><path d="M22 6h-4" />
    </svg>
);
const DiamondIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M2.7 10.3a2.41 2.41 0 0 0 0 3.4l8.3 8.3a2.41 2.41 0 0 0 3.4 0l8.3-8.3a2.41 2.41 0 0 0 0-3.4L14.4 2a2.41 2.41 0 0 0-3.4 0L2.7 10.3Z" />
    </svg>
);
const ArrowRightIcon = ({ className }: { className?: string }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
);

/* ═══════════════════════════════════════════════════════════════════════
   ANIMATED COUNTER
   ═══════════════════════════════════════════════════════════════════════ */
const AnimatedCounter: React.FC<{
    target: number;
    suffix?: string;
    prefix?: string;
    decimals?: number;
}> = ({ target, suffix = '', prefix = '', decimals = 0 }) => {
    const ref = useRef<HTMLSpanElement>(null);
    const inView = useInView(ref, { once: true, margin: '-60px' });
    const [count, setCount] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const dur = 2200;
        const start = performance.now();
        const tick = (now: number) => {
            const p = Math.min((now - start) / dur, 1);
            const ease = 1 - Math.pow(1 - p, 4); // easeOutQuart
            setCount(ease * target);
            if (p < 1) requestAnimationFrame(tick);
            else setCount(target);
        };
        requestAnimationFrame(tick);
    }, [inView, target]);

    return (
        <span ref={ref}>
            {prefix}{decimals > 0 ? count.toFixed(decimals) : Math.floor(count)}{suffix}
        </span>
    );
};

/* ═══════════════════════════════════════════════════════════════════════
   COMPARISON SLIDER COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
const ComparisonSlider = memo(() => {
    const [sliderPosition, setSliderPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMove = useCallback(
        (clientX: number) => {
            if (!containerRef.current) return;
            const rect = containerRef.current.getBoundingClientRect();
            const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
            const percent = (x / rect.width) * 100;
            setSliderPosition(percent);
        },
        []
    );

    const onMouseDown = () => setIsDragging(true);
    const onTouchStart = () => setIsDragging(true);

    useEffect(() => {
        const onMouseUp = () => setIsDragging(false);
        const onTouchEnd = () => setIsDragging(false);
        const onMouseMove = (e: MouseEvent) => {
            if (isDragging) handleMove(e.clientX);
        };
        const onTouchMove = (e: TouchEvent) => {
            if (isDragging) handleMove(e.touches[0].clientX);
        };

        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
            window.addEventListener('touchmove', onTouchMove);
            window.addEventListener('touchend', onTouchEnd);
        }

        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
            window.removeEventListener('touchmove', onTouchMove);
            window.removeEventListener('touchend', onTouchEnd);
        };
    }, [isDragging, handleMove]);

    return (
        <div
            className="relative w-full aspect-[4/3] md:aspect-[3/2] lg:aspect-square xl:aspect-[4/3] rounded-3xl overflow-hidden cursor-ew-resize select-none border border-white/10 shadow-2xl bg-[#09090b] group touch-none"
            ref={containerRef}
            onMouseDown={(e) => handleMove(e.clientX)}
            onTouchStart={(e) => handleMove(e.touches[0].clientX)}
        >
            {/* ── AFTER (Bottom Layer) ── */}
            <img
                src="/images/hero-back-side/after-hero.webp"
                alt="After transformation"
                className="absolute inset-0 w-full h-full object-contain pointer-events-none z-10 select-none bg-zinc-900/50"
                draggable={false}
            />
            {/* Label */}
            <div className="absolute top-6 right-6 z-20 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full border border-white/10 text-[10px] font-bold uppercase tracking-widest text-teal-400">
                After
            </div>

            {/* ── BEFORE (Top Layer, Clipped) ── */}
            <div
                className="absolute inset-0 z-20 pointer-events-none select-none bg-zinc-900/50"
                style={{
                    clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
                }}
            >
                <img
                    src="/images/hero-back-side/before-hero.webp"
                    alt="Before transformation"
                    className="absolute inset-0 w-full h-full object-contain pointer-events-none"
                    draggable={false}
                />
                {/* Label */}
                <div className="absolute top-6 left-6 z-30 px-3 py-1 bg-black/60 backdrop-blur-md rounded-full border border-white/10 text-[10px] font-bold uppercase tracking-widest text-white/70">
                    Before
                </div>
            </div>

            {/* ── HANDLE ── */}
            <div
                className="absolute top-0 bottom-0 w-1 bg-white z-30 cursor-ew-resize shadow-[0_0_20px_rgba(0,0,0,0.5)] group-hover:bg-teal-400 transition-colors"
                style={{ left: `${sliderPosition}%` }}
                onMouseDown={onMouseDown}
                onTouchStart={onTouchStart}
            >
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:scale-110 transition-transform md:w-12 md:h-12">
                    <svg className="w-5 h-5 text-black" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M15 19l-7-7 7-7" />
                        <path d="M19 12H5" className="hidden" />
                    </svg>
                    <svg className="w-5 h-5 text-black absolute rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M15 19l-7-7 7-7" />
                    </svg>
                </div>
            </div>
        </div>
    );
});

/* ═══════════════════════════════════════════════════════════════════════
   BENEFIT DATA
   ═══════════════════════════════════════════════════════════════════════ */
const benefits = [
    {
        num: '01',
        icon: ZapIcon,
        title: 'Speed That Converts',
        body: 'Create stunning smile designs in minutes, right at chairside. Engage patients at peak excitement—before they leave your office or have time to reconsider.',
        accent: '#f59e0b',
        gradient: 'from-amber-400 to-orange-500',
    },
    {
        num: '02',
        icon: TargetIcon,
        title: 'Unmatched Clinical Accuracy',
        body: "Our AI doesn't just make smiles look beautiful—it makes them achievable. Every design is realistic, naturally proportioned, and clinically viable, so you can confidently show patients exactly what they'll get.",
        accent: '#14b8a6',
        gradient: 'from-teal-400 to-emerald-500',
    },
    {
        num: '03',
        icon: SparklesIcon,
        title: 'See the Future, Today',
        body: 'Go beyond smile design with orthodontic treatment predictions. Show patients their transformation journey from day one, building trust and motivation that lasts through treatment.',
        accent: '#a78bfa',
        gradient: 'from-violet-400 to-purple-500',
    },
    {
        num: '04',
        icon: DiamondIcon,
        title: 'The Quality Difference',
        body: "We're not just the fastest—we're the best. Our advanced AI, trained on thousands of real cases, creates smile visualizations that look natural, feel authentic, and convert hesitant patients into committed cases.",
        accent: '#38bdf8',
        gradient: 'from-sky-400 to-blue-500',
    },
];

/* ═══════════════════════════════════════════════════════════════════════
   ANIMATION VARIANTS
   ═══════════════════════════════════════════════════════════════════════ */
const staggerContainer = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
};
const fadeSlideUp = {
    hidden: { opacity: 0, y: 50, filter: 'blur(8px)' },
    visible: {
        opacity: 1, y: 0, filter: 'blur(0px)',
        transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
    },
};

/* ═══════════════════════════════════════════════════════════════════════
   PROPS
   ═══════════════════════════════════════════════════════════════════════ */
interface TransformationSectionProps {
    onLaunchApp: () => void;
}

/* ═══════════════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════════════ */
const TransformationSection: React.FC<TransformationSectionProps> = memo(({ onLaunchApp }) => {
    return (
        <section className="relative py-36 md:py-44 overflow-hidden isolate" aria-label="Why Choose FuturaSmile">

            {/* ── Background Atmosphere ── */}
            <div className="absolute inset-0 pointer-events-none -z-10" aria-hidden="true">
                <div className="absolute inset-0 bg-grid opacity-[0.06]" />
                <div className="absolute -top-60 left-1/2 -translate-x-1/2 w-[1100px] h-[1100px] rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.10) 0%, transparent 65%)' }}
                />
                <div className="absolute -bottom-40 -left-20 w-[700px] h-[700px] rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.06) 0%, transparent 60%)' }}
                />
                <div className="absolute -bottom-40 -right-20 w-[600px] h-[600px] rounded-full"
                    style={{ background: 'radial-gradient(circle, rgba(56,189,248,0.05) 0%, transparent 60%)' }}
                />
                <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent" />
                <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/[0.07] to-transparent" />
            </div>

            <div className="container mx-auto px-6 lg:px-8 max-w-7xl relative z-10">

                {/* ═══════════════ HEADER ═══════════════ */}
                <div className="text-center max-w-5xl mx-auto mb-20 md:mb-28">
                    {/* Badge */}
                    <motion.div
                        initial={{ opacity: 0, y: 16, scale: 0.92 }}
                        whileInView={{ opacity: 1, y: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5 }}
                        className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full bg-white/[0.04] backdrop-blur-xl border border-white/[0.08] mb-10 shadow-[0_2px_20px_rgba(0,0,0,0.2)]"
                    >
                        <span className="relative flex h-2 w-2">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-60" />
                            <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-400" />
                        </span>
                        <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-teal-300/90">
                            Why Leading Practices Choose Us
                        </span>
                    </motion.div>

                    {/* Headline */}
                    <motion.h2
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.08, ease: [0.22, 1, 0.36, 1] }}
                        className="font-extrabold tracking-tight leading-[1.05] mb-8"
                        style={{ fontSize: 'clamp(2.8rem, 6vw, 5.5rem)' }}
                    >
                        <span className="text-white">Transform Every Consultation</span>
                        <br />
                        <span className="text-white">into a </span>
                        <span className="relative inline-block">
                            <span
                                className="text-transparent bg-clip-text"
                                style={{
                                    backgroundImage: 'linear-gradient(135deg, #5eead4 0%, #14b8a6 50%, #0d9488 100%)',
                                    filter: 'drop-shadow(0 0 24px rgba(45,212,191,0.35))',
                                }}
                            >
                                "Yes"
                            </span>
                            <motion.span
                                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                                style={{ backgroundImage: 'linear-gradient(90deg, transparent, rgba(45,212,191,0.6), transparent)' }}
                                initial={{ scaleX: 0 }}
                                whileInView={{ scaleX: 1 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.7, delay: 0.7 }}
                            />
                        </span>
                    </motion.h2>

                    {/* Subtitle */}
                    <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, delay: 0.18 }}
                        className="text-xl md:text-2xl text-zinc-400 font-light leading-relaxed max-w-3xl mx-auto"
                    >
                        The Digital Smile Design Platform That Closes Cases{' '}
                        <span className="text-white font-medium">Before You Even Scan</span>.
                    </motion.p>
                </div>

                {/* ═══════════════ SPLIT SECTION: QUOTE + SLIDER ═══════════════ */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-32 lg:mb-40">

                    {/* LEFT: Cinematic Quote */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        {/* Decorative quote mark */}
                        <div
                            className="absolute -top-10 -left-6 select-none pointer-events-none opacity-20"
                            style={{
                                fontSize: '8rem',
                                lineHeight: 1,
                                fontFamily: 'Georgia, serif',
                                background: 'linear-gradient(180deg, rgba(20,184,166,1) 0%, transparent 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                            }}
                        >
                            "
                        </div>

                        <div className="relative z-10">
                            <p className="text-2xl md:text-3xl lg:text-4xl text-zinc-200 font-light leading-[1.4] mb-8">
                                Imagine showing your patients their dream smile in{' '}
                                <strong className="text-teal-300 font-semibold">minutes—not weeks</strong>.
                            </p>
                            <p className="text-xl text-zinc-500 font-light leading-relaxed">
                                With AI trained on thousands of real smiles, our platform delivers
                                chairside visualizations so natural and clinically accurate that patients{' '}
                                <strong className="text-white font-medium">can't help but say yes</strong>.
                            </p>
                        </div>
                    </motion.div>

                    {/* RIGHT: Visual Slider */}
                    <motion.div
                        initial={{ opacity: 0, x: 30, scale: 0.95 }}
                        whileInView={{ opacity: 1, x: 0, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                    >
                        <ComparisonSlider />
                        <div className="flex justify-center mt-4 gap-2 text-xs text-zinc-500 font-medium uppercase tracking-widest">
                            <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-teal-500"></span> Draggable Comparison</span>
                        </div>
                    </motion.div>

                </div>


                {/* ═══════════════ BENEFITS ═══════════════ */}
                <motion.div
                    variants={staggerContainer}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: '-50px' }}
                    className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 mb-32"
                >
                    {benefits.map((b) => (
                        <motion.div
                            key={b.num}
                            variants={fadeSlideUp}
                            className="group relative rounded-[1.75rem] overflow-hidden"
                        >
                            {/* Animated border gradient */}
                            <div
                                className="absolute inset-0 rounded-[1.75rem] p-px opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                                style={{
                                    background: `linear-gradient(135deg, ${b.accent}33 0%, transparent 40%, transparent 60%, ${b.accent}22 100%)`,
                                }}
                            />

                            {/* Card body */}
                            <div className="relative h-full bg-white/[0.025] backdrop-blur-xl rounded-[1.75rem] border border-white/[0.06] group-hover:border-white/[0.12] transition-all duration-500 overflow-hidden">

                                {/* Mouse-follow radial glow */}
                                <div
                                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none"
                                    style={{
                                        background: `radial-gradient(600px circle at 50% 0%, ${b.accent}12, transparent 60%)`,
                                    }}
                                />

                                {/* Top accent bar */}
                                <div
                                    className="absolute top-0 inset-x-0 h-px opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                                    style={{ backgroundImage: `linear-gradient(90deg, transparent, ${b.accent}88, transparent)` }}
                                />

                                <div className="relative z-10 p-8 lg:p-10">
                                    {/* Number + Icon row */}
                                    <div className="flex items-center justify-between mb-8">
                                        {/* Number */}
                                        <span
                                            className="text-[4.5rem] font-black leading-none select-none"
                                            style={{
                                                background: `linear-gradient(180deg, ${b.accent}18 0%, transparent 100%)`,
                                                WebkitBackgroundClip: 'text',
                                                WebkitTextFillColor: 'transparent',
                                            }}
                                        >
                                            {b.num}
                                        </span>

                                        {/* Icon capsule */}
                                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${b.gradient} p-px shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-500`}>
                                            <div className="w-full h-full rounded-2xl bg-zinc-950/90 backdrop-blur-sm flex items-center justify-center">
                                                <b.icon className="w-6 h-6 text-white/90" />
                                            </div>
                                        </div>
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-2xl lg:text-[1.65rem] font-bold text-white tracking-tight mb-4 group-hover:text-white transition-colors">
                                        {b.title}
                                    </h3>

                                    {/* Body */}
                                    <p className="text-zinc-400 text-base lg:text-[1.05rem] leading-[1.8] group-hover:text-zinc-300 transition-colors duration-500">
                                        {b.body}
                                    </p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </motion.div>

                {/* ═══════════════ RESULTS PANEL ═══════════════ */}
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.97 }}
                    whileInView={{ opacity: 1, y: 0, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="relative max-w-5xl mx-auto"
                >
                    {/* Ambient glow behind panel */}
                    <div className="absolute -inset-12 rounded-[3rem] pointer-events-none"
                        style={{ background: 'radial-gradient(circle at 50% 40%, rgba(20,184,166,0.08) 0%, transparent 60%)' }}
                    />

                    {/* Panel */}
                    <div className="relative rounded-[2.5rem] overflow-hidden">
                        {/* Gradient border */}
                        <div className="absolute inset-0 rounded-[2.5rem] p-px bg-gradient-to-b from-white/[0.12] via-white/[0.04] to-white/[0.06] pointer-events-none" />

                        <div className="relative bg-zinc-950/60 backdrop-blur-2xl rounded-[2.5rem] px-8 md:px-16 lg:px-20 py-16 md:py-20 lg:py-24">
                            {/* Top accent */}
                            <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-teal-400/40 to-transparent" />

                            <div className="text-center">
                                {/* Label */}
                                <span className="text-teal-400 font-mono text-xs tracking-[0.25em] uppercase font-bold flex items-center justify-center gap-4 mb-8">
                                    <span className="w-8 h-px bg-teal-500/40" />
                                    The Bottom Line
                                    <span className="w-8 h-px bg-teal-500/40" />
                                </span>

                                <h3
                                    className="font-extrabold text-white tracking-tight leading-[1.1] mb-6"
                                    style={{ fontSize: 'clamp(2.2rem, 4.5vw, 3.8rem)' }}
                                >
                                    The Result?
                                </h3>

                                <p className="text-xl md:text-2xl text-zinc-400 font-light mb-16 max-w-2xl mx-auto">
                                    More cases accepted. Faster decisions. Happier patients.
                                </p>

                                {/* Stats row */}
                                <div className="grid grid-cols-3 gap-4 md:gap-8 mb-16 max-w-3xl mx-auto">
                                    {[
                                        { value: 73, suffix: '%', prefix: '+', label: 'Case Acceptance' },
                                        { value: 2, suffix: 'x', prefix: '', label: 'Faster Decisions' },
                                        { value: 98, suffix: '%', prefix: '', label: 'Patient Satisfaction' },
                                    ].map((s, i) => (
                                        <div key={i} className="relative text-center">
                                            <div
                                                className="text-4xl md:text-5xl lg:text-6xl font-black tabular-nums tracking-tight mb-2"
                                                style={{
                                                    background: 'linear-gradient(180deg, #fff 20%, #5eead4 100%)',
                                                    WebkitBackgroundClip: 'text',
                                                    WebkitTextFillColor: 'transparent',
                                                }}
                                            >
                                                <AnimatedCounter target={s.value} suffix={s.suffix} prefix={s.prefix} />
                                            </div>
                                            <div className="text-[11px] md:text-xs text-zinc-500 font-semibold uppercase tracking-[0.15em]">
                                                {s.label}
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Divider */}
                                <div className="w-full max-w-xs mx-auto h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent mb-14" />

                                {/* Closing copy */}
                                <p className="text-lg md:text-xl text-zinc-500 mb-12 max-w-2xl mx-auto leading-relaxed">
                                    Stop losing cases to consideration time. Start converting consultations into commitments—
                                    <span className="text-zinc-300">before patients even reach the scanner.</span>
                                </p>

                                {/* CTA Button */}
                                <div className="flex flex-col items-center gap-8">
                                    <button
                                        onClick={onLaunchApp}
                                        className="group relative inline-flex items-center gap-3 px-12 py-5 rounded-full font-bold text-lg transition-all duration-300 will-change-transform focus:outline-none focus-visible:ring-4 focus-visible:ring-teal-400/50 hover:-translate-y-0.5 hover:scale-[1.03] active:scale-[0.98]"
                                        style={{
                                            background: 'linear-gradient(135deg, #2dd4bf 0%, #14b8a6 50%, #0d9488 100%)',
                                            color: '#050505',
                                            boxShadow: '0 0 0 1px rgba(45,212,191,0.3), 0 4px 30px rgba(45,212,191,0.35), 0 20px 60px -12px rgba(45,212,191,0.25)',
                                        }}
                                    >
                                        <span className="relative z-10 flex items-center gap-3">
                                            Start Transforming Now
                                            <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1.5 transition-transform duration-300" />
                                        </span>
                                        {/* Hover shine */}
                                        <div className="absolute inset-0 rounded-full bg-white/0 group-hover:bg-white/20 transition-colors duration-300" />
                                    </button>

                                    {/* Trust signals */}
                                    <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[13px] text-zinc-600">
                                        {[
                                            'No credit card required',
                                            'Setup in 30 seconds',
                                            'HIPAA Compliant',
                                        ].map((t) => (
                                            <div key={t} className="flex items-center gap-2">
                                                <svg className="w-4 h-4 text-teal-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
                                                </svg>
                                                <span>{t}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>

            </div>

            {/* ── Inline keyframes ── */}
            <style>{`
        @keyframes float-slow {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50% { transform: translateY(-12px) rotate(2deg); }
        }
      `}</style>
        </section>
    );
});

TransformationSection.displayName = 'TransformationSection';

export default TransformationSection;
